import { ResponseError } from '@backstage/errors';
import {
  CiPipeline,
  CiPipelinesApi,
  CiRun,
  TriggerCiOptions,
} from './CiPipelinesApi';
import { WorkflowPhase } from './types';

interface FetchApi {
  fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

interface DiscoveryApi {
  getBaseUrl(pluginId: string): Promise<string>;
}

const TEMPLATE_LABEL = 'workflows.argoproj.io/workflow-template';
const IAC_TEMPLATE_PATTERN = /^(terraform-|terragrunt-)/;
const NS_CACHE_TTL = 5 * 60 * 1000;

// Metadata overrides for well-known templates. When a template matches by name
// here, its label/description/param hints are used instead of raw API data.
const KNOWN_METADATA: Record<string, { label: string; description: string; params?: CiPipeline['params'] }> = {
  'deki-build-pipeline-web': {
    label: 'Deki Web',
    description: 'mindtouch-web / httpd / node-services images (arm64).',
    params: [
      { key: 'repo_url', label: 'Repo URL', defaultValue: 'https://github.com/MindTouch/Deki.git' },
      { key: 'branch', label: 'Branch', defaultValue: 'master' },
      { key: 'commit', label: 'Commit (blank = branch HEAD)', defaultValue: '' },
      { key: 'push_enabled', label: 'Push to ECR', defaultValue: 'false', type: 'boolean' },
    ],
  },
  'deki-build-pipeline-dotnet': {
    label: 'Deki .NET',
    description: 'mindtouch-dotnet image from the Deki solution (amd64).',
    params: [
      { key: 'repo_url', label: 'Repo URL', defaultValue: 'https://github.com/MindTouch/Deki.git' },
      { key: 'branch', label: 'Branch', defaultValue: 'master' },
      { key: 'commit', label: 'Commit (blank = branch HEAD)', defaultValue: '' },
      { key: 'push_enabled', label: 'Push to ECR', defaultValue: 'false', type: 'boolean' },
      { key: 'build_all', label: 'Build all projects', defaultValue: 'false', type: 'boolean' },
    ],
  },
  'self-service-build': {
    label: 'Backstage (this app)',
    description: 'Self-service portal image (arm64).',
    params: [
      { key: 'repo_url', label: 'Repo URL', defaultValue: 'https://github.com/MindTouch/backstage.git' },
      { key: 'branch', label: 'Branch', defaultValue: 'master' },
      { key: 'commit', label: 'Commit (blank = branch HEAD)', defaultValue: '' },
      { key: 'push_enabled', label: 'Push to ECR', defaultValue: 'true', type: 'boolean' },
      { key: 'release_enabled', label: 'Semantic release', defaultValue: 'true', type: 'boolean' },
    ],
  },
  'litellm-headroom-build': {
    label: 'LiteLLM Headroom',
    description: 'litellm-headroom-mt image.',
    params: [
      { key: 'repo_url', label: 'Repo URL', defaultValue: 'https://github.com/MindTouch/litellm.git' },
      { key: 'branch', label: 'Branch', defaultValue: 'litellm_internal_staging' },
      { key: 'commit', label: 'Commit (blank = branch HEAD)', defaultValue: '' },
      { key: 'push_enabled', label: 'Push to ECR', defaultValue: 'true', type: 'boolean' },
      { key: 'release_enabled', label: 'Semantic release', defaultValue: 'true', type: 'boolean' },
    ],
  },
  'docker-base-image-build': {
    label: 'Docker Base Image',
    description: 'Base/utility images (e.g. twemproxy) from the Deki repo.',
    params: [
      { key: 'repo_url', label: 'Repo URL', defaultValue: 'https://github.com/MindTouch/Deki.git' },
      { key: 'branch', label: 'Branch', defaultValue: 'master' },
      { key: 'commit', label: 'Commit (blank = branch HEAD)', defaultValue: '' },
      { key: 'dockerfile_path', label: 'Dockerfile path', defaultValue: 'Dockerfile-mindtouch-twemproxy' },
      { key: 'ecr_repo', label: 'ECR repo', defaultValue: '366391106850.dkr.ecr.us-east-1.amazonaws.com/twemproxy' },
      { key: 'push_enabled', label: 'Push to ECR', defaultValue: 'false', type: 'boolean' },
    ],
  },
  'mcp-gateway-build': {
    label: 'MCP Gateway',
    description: 'MCP Gateway server image.',
  },
  'oncall-hand-build': {
    label: 'Oncall Hand',
    description: 'Oncall hand-off assistant image.',
  },
  'devops-fang-build': {
    label: 'DevOps FANG',
    description: 'DevOps FANG tooling image.',
  },
  'k6-run': {
    label: 'k6 Load Test',
    description: 'Run k6 load/performance tests.',
  },
  'ua-knowledge-graph': {
    label: 'UA Knowledge Graph',
    description: 'Understand-Anything knowledge graph generation.',
  },
  'trigger-deploy-pipeline': {
    label: 'Trigger Deploy',
    description: 'Trigger a CD deployment pipeline.',
  },
  'trigger-bootstrap-do-release': {
    label: 'Bootstrap Release Params',
    description: 'Update do-release default parameters for lower envs via bootstrap pipelines.',
    params: [
      { key: 'environments', label: 'Environments (comma-separated)', defaultValue: 'staging,gamma,au1stage,ca1stage' },
      { key: 'PREVIOUS_RELEASE', label: 'Previous Release', defaultValue: '' },
      { key: 'TARGET_RELEASE', label: 'Target Release', defaultValue: '' },
      { key: 'CONTINUOUS_DELIVERY_BRANCH', label: 'CD Branch (blank = TARGET_RELEASE)', defaultValue: '' },
      { key: 'dry_run', label: 'Dry Run', defaultValue: 'false', type: 'boolean' },
    ],
  },
  'trigger-do-release': {
    label: 'Trigger Do-Release',
    description: 'Run do-release pipeline for lower envs (alpha/staging).',
    params: [
      { key: 'environments', label: 'Environments (comma-separated)', defaultValue: 'staging,gamma,au1stage,ca1stage' },
      { key: 'PREVIOUS_RELEASE', label: 'Previous Release', defaultValue: '' },
      { key: 'TARGET_RELEASE', label: 'Target Release', defaultValue: '' },
      { key: 'firstStage', label: 'First Stage', defaultValue: '1.Post Release Notice' },
      { key: 'lastStage', label: 'Last Stage', defaultValue: '22.Complete Release' },
      { key: 'GIT_SHA', label: 'Git SHA (optional)', defaultValue: '' },
      { key: 'DEKI_BRANCH', label: 'Deki Branch (optional)', defaultValue: '' },
      { key: 'CONTINUOUS_DELIVERY_BRANCH', label: 'CD Branch (blank = TARGET_RELEASE)', defaultValue: '' },
      { key: 'dry_run', label: 'Dry Run', defaultValue: 'false', type: 'boolean' },
    ],
  },
  'embedding-service-build': {
    label: 'UKS Embedding Service',
    description: 'Embedding service image for UKS.',
  },
  'ocr-server-build': {
    label: 'UKS OCR Server',
    description: 'OCR server image for UKS.',
  },
  'promote-uks': {
    label: 'Promote UKS',
    description: 'Promote UKS to target environment.',
  },
  'promote-uks-feature': {
    label: 'Promote UKS Feature',
    description: 'Promote UKS feature branch environment.',
  },
  'promote-embedding': {
    label: 'Promote Embedding',
    description: 'Promote embedding service to target environment.',
  },
  'promote-ocr-server': {
    label: 'Promote OCR Server',
    description: 'Promote OCR server to target environment.',
  },
  'promote-docling': {
    label: 'Promote Docling',
    description: 'Promote Docling service to target environment.',
  },
  'teardown-uks-feature': {
    label: 'Teardown UKS Feature',
    description: 'Tear down a UKS feature branch environment.',
  },
};

export class CiPipelinesClient implements CiPipelinesApi {
  private readonly fetchApi: FetchApi;
  private readonly discoveryApi: DiscoveryApi;
  private nsCache: { promise: Promise<string[]>; ts: number } | null = null;

  constructor(deps: { fetchApi: FetchApi; discoveryApi: DiscoveryApi }) {
    this.fetchApi = deps.fetchApi;
    this.discoveryApi = deps.discoveryApi;
  }

  private async base(): Promise<string> {
    const proxy = await this.discoveryApi.getBaseUrl('proxy');
    return `${proxy}/argo-workflows`;
  }

  private async resolveCiNamespaces(): Promise<string[]> {
    if (this.nsCache && Date.now() - this.nsCache.ts < NS_CACHE_TTL) {
      return this.nsCache.promise;
    }
    const promise = this.doResolveCiNamespaces();
    this.nsCache = { promise, ts: Date.now() };
    promise.catch(() => { this.nsCache = null; });
    return promise;
  }

  private async doResolveCiNamespaces(): Promise<string[]> {
    const base = await this.base();
    const res = await this.fetchApi.fetch(`${base}/api/v1/workflow-templates/`);
    if (!res.ok) return ['ci', 'ci-uks'];
    const json = (await res.json()) as { items?: ArgoTemplateObj[] };
    const items = json.items ?? [];
    const iacNamespaces = new Set<string>();
    for (const t of items) {
      if (IAC_TEMPLATE_PATTERN.test(t.metadata.name)) {
        iacNamespaces.add(t.metadata.namespace);
      }
    }
    const allNamespaces = [...new Set(items.map(t => t.metadata.namespace))];
    return allNamespaces.filter(ns => !iacNamespaces.has(ns)).sort();
  }

  async listPipelines(): Promise<CiPipeline[]> {
    const base = await this.base();
    const namespaces = await this.resolveCiNamespaces();
    const results = await Promise.all(
      namespaces.map(async ns => {
        const url = `${base}/api/v1/workflow-templates/${encodeURIComponent(ns)}`;
        const res = await this.fetchApi.fetch(url);
        if (!res.ok) return [];
        const json = (await res.json()) as { items?: ArgoTemplateObj[] };
        return (json.items ?? []).map(t => templateToPipeline(t, ns));
      }),
    );
    return results.flat().sort((a, b) => a.label.localeCompare(b.label));
  }

  async listRuns(): Promise<CiRun[]> {
    const base = await this.base();
    const namespaces = await this.resolveCiNamespaces();
    const results = await Promise.all(
      namespaces.map(async ns => {
        const url = new URL(`${base}/api/v1/workflows/${encodeURIComponent(ns)}`);
        url.searchParams.set('listOptions.limit', '100');
        const res = await this.fetchApi.fetch(url.toString());
        if (!res.ok) return [];
        const json = (await res.json()) as { items?: ArgoWorkflowObj[] };
        return json.items ?? [];
      }),
    );
    return results
      .flat()
      .map(wf => {
        const params = wf.spec?.arguments?.parameters ?? [];
        const param = (n: string) => params.find(p => p.name === n)?.value;
        return {
          workflowName: wf.metadata.name,
          namespace: wf.metadata.namespace,
          uid: wf.metadata.uid,
          phase: wf.status?.phase ?? 'Unknown',
          template: templateOf(wf),
          startedAt: wf.status?.startedAt,
          finishedAt: wf.status?.finishedAt,
          branch: param('branch') || param('git_branch'),
          commit: param('commit') || param('git_sha'),
        } as CiRun;
      })
      .sort((a, b) => cmpDate(b.startedAt, a.startedAt));
  }

  async triggerBuild(opts: TriggerCiOptions): Promise<{ namespace: string; name: string }> {
    const ns = opts.pipeline.namespace ?? (await this.resolveCiNamespaces())[0] ?? 'ci';
    const parameters = Object.entries(opts.parameters)
      .map(([name, value]) => ({ name, value }));

    const actionsBase = await this.discoveryApi.getBaseUrl('argo-actions');
    const res = await this.fetchApi.fetch(
      `${actionsBase}/argo/submit/${encodeURIComponent(ns)}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateName: opts.pipeline.template,
          generateName: `${opts.pipeline.template}-`,
          labels: { [TEMPLATE_LABEL]: opts.pipeline.template },
          parameters,
        }),
      },
    );
    if (!res.ok) throw await ResponseError.fromResponse(res);
    const json = (await res.json()) as { metadata: { namespace: string; name: string } };
    return { namespace: json.metadata.namespace, name: json.metadata.name };
  }
}

interface ArgoTemplateObj {
  metadata: { namespace: string; name: string };
  spec?: {
    arguments?: {
      parameters?: { name: string; default?: string; value?: string; description?: string }[];
    };
  };
}

interface ArgoWorkflowObj {
  metadata: {
    namespace: string;
    name: string;
    uid: string;
    labels?: Record<string, string>;
    generateName?: string;
  };
  spec?: {
    workflowTemplateRef?: { name?: string };
    arguments?: { parameters?: { name: string; value?: string }[] };
  };
  status?: {
    phase?: WorkflowPhase;
    startedAt?: string;
    finishedAt?: string;
  };
}

function templateToPipeline(t: ArgoTemplateObj, ns: string): CiPipeline {
  const known = KNOWN_METADATA[t.metadata.name];
  const apiParams: CiPipeline['params'] = (t.spec?.arguments?.parameters ?? []).map(p => ({
    key: p.name,
    label: p.description || p.name,
    defaultValue: p.default ?? p.value ?? '',
    type: (p.default === 'true' || p.default === 'false') ? 'boolean' as const : 'text' as const,
  }));
  return {
    id: `${ns}/${t.metadata.name}`,
    label: known?.label ?? humanize(t.metadata.name),
    description: known?.description ?? `Workflow template in ${ns}`,
    template: t.metadata.name,
    namespace: ns,
    params: known?.params ?? apiParams,
  };
}

function humanize(name: string): string {
  return name
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

// Best-effort template identity: prefer the label, then workflowTemplateRef,
// then strip the trailing `-<suffix>` off generateName/name.
function templateOf(wf: ArgoWorkflowObj): string {
  const label = wf.metadata.labels?.[TEMPLATE_LABEL];
  if (label) return label;
  const ref = wf.spec?.workflowTemplateRef?.name;
  if (ref) return ref;
  const gen = wf.metadata.generateName?.replace(/-$/, '');
  if (gen) return gen;
  return wf.metadata.name.replace(/-[a-z0-9]{5}$/, '');
}

function cmpDate(a?: string, b?: string): number {
  return (a ? Date.parse(a) : 0) - (b ? Date.parse(b) : 0);
}
